import { useEffect } from 'react';
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@components/layout';
import GlobalLoader from '@components/ui/GlobalLoader/GlobalLoader';
import RouteProgressBar from '@components/shared/RouteProgressBar/RouteProgressBar';
import { getSession } from '@/services/authService';
import { getStringBundle } from '@/services/localizationService';
import { getEffectiveNavigation } from '@/services/navigationService';
import { useAuthStore } from '@/stores/authStore';
import { useLocalizationStore } from '@/stores/localizationStore';
import { useAuthBroadcast, broadcastLoggedIn } from '@/hooks/useAuthBroadcast';
import { AppDsProvider } from '@lib/dsProvider';
import { EntityBlockPage } from '@components/shared/EntityBlockPage/EntityBlockPage';

// Ensure staticData type augmentation is loaded
import '@lib/routerTypes';

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const matches = useRouterState({ select: (s) => s.matches });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showShell = !matches.some(
    (m) => (m.staticData as Record<string, unknown> | undefined)?.hideShell,
  );

  const setSession = useAuthStore((s) => s.setSession);
  const session = useAuthStore((s) => s.session);
  const userType = useAuthStore((s) => s.userType);
  const language = useLocalizationStore((s) => s.language);
  const setLanguage = useLocalizationStore((s) => s.setLanguage);

  // Detect hard refresh via Navigation Timing API. When true, ?sync=true is appended
  // to GET /api/session so the BFF re-fetches live platform data (permissions, org/tenant
  // status) from platform-service and updates Redis before returning the session.
  const isPageReload = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type === 'reload';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['session'],
    queryFn: () => getSession(isPageReload),
    retry: false,
  });

  // Listen for logout/session-expired/logged-in from other tabs
  useAuthBroadcast();

  useEffect(() => {
    // Skip setSession for ENTITY_SESSION_ACTIVE responses — they don't have
    // the user/organization/tenant shape and would crash the auth store.
    const rawData = data as Record<string, unknown> | undefined;
    if (rawData?.status === 'ENTITY_SESSION_ACTIVE') return;

    if (data && !session) {
      setSession(data);
      // Notify other tabs that a session is now active
      broadcastLoggedIn();

      // Initialize locale from session if localStorage has no preference
      const storedLang = localStorage.getItem('wp_language');
      if (!storedLang && data.user.locale) {
        setLanguage(data.user.locale);
      }
    }
  }, [data, session, setSession, setLanguage]);

  const organizationId = session?.organization.id ?? undefined;
  const isEmployee = userType === 'EMPLOYEE' || pathname.startsWith('/e');

  // Localization string bundle. Gated on the session; shares its query key with
  // useTranslation, so it dedupes to a single fetch. The boot loader waits on
  // this so the UI never flashes English-fallback strings before the localized
  // bundle is applied.
  const localizationQuery = useQuery({
    queryKey: ['localization', 'bundle', language, organizationId ?? ''],
    queryFn: () => getStringBundle(language, organizationId),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    enabled: !!session,
  });

  // Single source of truth for sidebar nav. AppShell receives the data via
  // prop — do NOT add a second useQuery there. staleTime: Infinity defends
  // against the project-wide default of 0.
  const navigationQuery = useQuery({
    queryKey: ['effectiveNavigation'],
    queryFn: () => getEffectiveNavigation(),
    enabled: !!session && !isEmployee,
    staleTime: Infinity,
  });

  // Session fetch failed — the envelope interceptor's handleSessionExpiry()
  // has already redirected to /login. Render nothing while the browser navigates.
  if (isError) {
    return null;
  }

  const entityActiveData = data as Record<string, unknown> | undefined;
  if (entityActiveData?.status === 'ENTITY_SESSION_ACTIVE') {
    return (
      <AppDsProvider>
        <EntityBlockPage
          entityName={entityActiveData.entityName as string}
          entityUrl={entityActiveData.entityUrl as string}
        />
      </AppDsProvider>
    );
  }

  // Boot gate — hold the branded loader until the session, the localization
  // bundle, and (when the shell is shown) the navigation config have all
  // settled and been applied. Without this, first paint flickers: English
  // fallbacks → localized strings, and a static → API-driven sidebar.
  const sessionReady = !!session;
  const localizationReady = localizationQuery.isFetched;
  const navigationReady = !showShell || isEmployee || navigationQuery.isFetched;

  if (isLoading || !sessionReady || !localizationReady || !navigationReady) {
    return <GlobalLoader />;
  }

  if (!showShell) {
    return (
      <AppDsProvider>
        <RouteProgressBar />
        <Outlet />
        {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
      </AppDsProvider>
    );
  }

  return (
    <AppDsProvider>
      <RouteProgressBar />
      <AppShell effectiveNav={navigationQuery.data}>
        <Outlet />
      </AppShell>
      {/* {import.meta.env.DEV && location.hostname === 'localhost' && <TanStackRouterDevtools position="bottom-right" />} */}
    </AppDsProvider>
  );
}
